import { useRef } from 'react';
import { NO_OF_CONCURRENT_API_CALLS } from 'src/config';
import type { FilesUploadStatus } from 'src/content/Documents/utils/utils';
import { getFormDataNew } from 'src/content/Documents/utils/utils';
import type { UPLOAD_TYPE } from 'src/types/enum';
import { groupFilesBySize } from 'src/utils/files/file_chunking';

interface UploadDocumentsConfig {
  uploadApiFn: (
    formData: FormData,
    abortController?: { abort: () => void },
    getFormData?: () => Promise<FormData>
  ) => Promise<any>;
  parentFolderId: string | number;
  concurrentCalls?: number;

  singleFileCancel?: boolean;
  extraFormDataArgs?:
    | Record<string, any>
    | (() => Record<string, any> | undefined);

  onBatchPreparing?: (filesList: File[]) => void;
  onUploadStart?: (filesList: File[]) => void;
  onBatchSuccess?: (filesList: File[], responseData: any) => void;
  onBatchError?: (filesList: File[], error?: any) => void;
  onUploadComplete?: (
    successCount: number,
    failedCount: number,
    retrials: File[][]
  ) => void;
}

export const useUploadDocuments = (config: UploadDocumentsConfig) => {
  const transactionIdRef = useRef<string | undefined>(undefined);
  const isCancelledRef = useRef(false);

  const {
    uploadApiFn,
    parentFolderId,
    concurrentCalls = NO_OF_CONCURRENT_API_CALLS,
    extraFormDataArgs = {},
    singleFileCancel = false,
    onBatchPreparing,
    onUploadStart,
    onBatchSuccess,
    onBatchError
  } = config;
  const activeRequests = useRef<Set<{ abort: () => void }>>(new Set());
  const activeRequestsByFile = useRef<Map<File, { abort: () => void }>>(
    new Map()
  );

  const uploadApiCall = async (
    filesList: File[],
    currentUploadType?: UPLOAD_TYPE
  ): Promise<FilesUploadStatus> => {
    const abortContainer = { abort: () => {} };
    try {
      activeRequests.current.add(abortContainer);
      if (singleFileCancel) {
        filesList.forEach((f) =>
          activeRequestsByFile.current.set(f, abortContainer)
        );
      }
      onBatchPreparing?.(filesList);

      const buildFormData = () => {
        const resolvedExtra =
          typeof extraFormDataArgs === 'function'
            ? extraFormDataArgs() ?? {}
            : extraFormDataArgs ?? {};
        return getFormDataNew(
          filesList,
          transactionIdRef.current,
          currentUploadType,
          parentFolderId,
          resolvedExtra
        );
      };
      const formData = await buildFormData();

      onUploadStart?.(filesList);
      const response = await uploadApiFn(
        formData,
        abortContainer,
        buildFormData
      );
      onBatchSuccess?.(filesList, response);
      return { success: [filesList.length], failed: [] };
    } catch (error) {
      onBatchError?.(filesList, error);
      return { success: [], failed: [filesList.length], details: filesList };
    } finally {
      activeRequests.current.delete(abortContainer);
      if (singleFileCancel) {
        filesList.forEach((f) => activeRequestsByFile.current.delete(f));
      }
    }
  };

  const uploadWithQueue = async (
    fileArray: File[][],
    currentUploadType?: UPLOAD_TYPE
  ) => {
    isCancelledRef.current = false;
    let currentIndex = 0;
    const successFiles: number[] = [];
    const failedFiles: number[] = [];
    const retrials: File[][] = [];

    const uploadNext = async (): Promise<void> => {
      if (isCancelledRef.current) {
        return;
      }

      const index = currentIndex++;
      if (index >= fileArray.length) {
        return;
      }

      const currentFileGroup = fileArray[index];
      const result = await uploadApiCall(currentFileGroup, currentUploadType);

      if (result) {
        successFiles.push(...result.success);
        failedFiles.push(...result.failed);
        if (result.details) {
          retrials.push(result.details);
        }
      }
      await uploadNext();
    };
    const workers = Array.from({ length: concurrentCalls }, () => uploadNext());
    await Promise.all(workers);

    return { successFiles, failedFiles, retrials };
  };

  const uploadGroupDocs = async (
    acceptedFiles: File[],
    transactionId: string,
    currentUploadType?: UPLOAD_TYPE
  ) => {
    transactionIdRef.current = transactionId;

    if (acceptedFiles.length === 0) {
      return { successCount: 0, failedCount: 0, retrials: [] };
    }

    const plannedGroups: File[][] = groupFilesBySize(
      acceptedFiles,
      false
    ) as File[][];

    const { successFiles, failedFiles, retrials } = await uploadWithQueue(
      plannedGroups,
      currentUploadType
    );

    const successCount = successFiles.reduce((acc, curr) => acc + curr, 0);
    const failedCount = failedFiles.reduce((acc, curr) => acc + curr, 0);

    config?.onUploadComplete?.(successCount, failedCount, retrials);

    return { successCount, failedCount, retrials };
  };

  const retryFailedFiles = async (
    failedFiles: File[],
    uploadType?: UPLOAD_TYPE
  ) => {
    const asyncGroups: File[][] = groupFilesBySize(
      failedFiles,
      false
    ) as File[][];
    const {
      failedFiles: newFailed,
      successFiles,
      retrials: newRetrials
    } = await uploadWithQueue(asyncGroups, uploadType);

    const successCount = successFiles.reduce((acc, curr) => acc + curr, 0);
    const failedCount = newFailed.reduce((acc, curr) => acc + curr, 0);

    config?.onUploadComplete?.(successCount, failedCount, newRetrials);
  };

  const abortAllActiveRequests = () => {
    isCancelledRef.current = true;
    activeRequests.current.forEach((c) => c.abort());
    activeRequests.current.clear();
  };

  const abortFiles = (files: File[]) => {
    const containers = new Set<{ abort: () => void }>();
    files.forEach((f) => {
      const container = activeRequestsByFile.current.get(f);
      if (container) containers.add(container);
    });
    containers.forEach((c) => c.abort());
  };

  return {
    uploadGroupDocs,
    retryFailedFiles,
    abortAllActiveRequests,
    abortFiles
  };
};
